import { parseCanonicalResume, type CanonicalResume } from '../schemas/resumeSchema';

export interface ProcessResumeInput {
  fileBase64?: string;
  mimeType?: string;
  resumeText?: string;
  jobDescription?: string;
  signal?: AbortSignal;
}

function resolveFunctionUrl(): string {
  const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';
  return `${base}/functions/v1/extract-canonical-resume`;
}

/**
 * Client SDK for the canonical resume pipeline.
 * Gemini runs on the edge function — never call Google from the browser.
 */
export async function processResumeWithGemini(
  input: ProcessResumeInput,
): Promise<CanonicalResume> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!supabaseUrl || !anonKey) {
    throw new Error('Supabase is not configured');
  }

  const hasFile = Boolean(input.fileBase64 && input.mimeType);
  const hasText = Boolean(input.resumeText?.trim());
  if (!hasFile && !hasText) {
    throw new Error('Provide a resume file or resume text');
  }

  const resp = await fetch(resolveFunctionUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify({
      fileBase64: input.fileBase64,
      mimeType: input.mimeType,
      resumeText: input.resumeText,
      jobDescription: input.jobDescription,
    }),
    signal: input.signal ?? AbortSignal.timeout(30_000),
  });

  const payload = (await resp.json().catch(() => ({}))) as {
    resume?: unknown;
    error?: string;
  };

  if (!resp.ok) {
    throw new Error(payload.error || `extract-canonical-resume ${resp.status}`);
  }

  return parseCanonicalResume(payload.resume);
}
